import createDebug from 'debug';
import fuzzysort from 'fuzzysort';
import { Tags } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TagResponse } from '@/api/client';
import { decodeGlobalId } from '@/lib/global-id';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import MultipleSelector, { type Option } from '@/components/ui/multiple-selector';
import { useAddTagsToLinkMutation, useRemoveTagFromLinkMutation } from '@/hooks/useLinks';
import { useTagsQuery } from '@/hooks/useGraphQLQueries';

const debug = createDebug('app:tag-editor');

// No-op for backwards compatibility - urql's graphcache handles invalidation automatically
export function invalidateTagCache(): void {
	// urql's normalized cache is invalidated via mutations and subscriptions
}

interface TagEditorProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	linkId: number;
	currentTags: TagResponse[];
	onTagsChanged: (tags: TagResponse[]) => void;
}

// Convert TagResponse to Option for MultipleSelector
function tagToOption(tag: TagResponse): Option {
	return {
		value: tag.name,
		label: formatTagLabel(tag.name),
		color: tag.color ?? undefined,
	};
}

// Format tag name for display
function formatTagLabel(name: string): string {
	if (name.startsWith('category:')) {
		return name.replace('category:', '');
	}
	if (name.startsWith('domain:')) {
		return name.replace('domain:', '');
	}
	if (name.startsWith('reddit:u:')) {
		return `u/${name.replace('reddit:u:', '')}`;
	}
	if (name.startsWith('reddit:r:')) {
		return `r/${name.replace('reddit:r:', '')}`;
	}
	return name;
}

// Get tag prefix for grouping
function getTagGroup(name: string): string {
	if (name.startsWith('category:')) return 'Categories';
	if (name.startsWith('domain:')) return 'Domains';
	if (name.startsWith('reddit:')) return 'Reddit';
	return 'Other';
}

// Extract the "value" part from prefixed tags (e.g., "reddit.com" from "domain:reddit.com")
function getTagValue(name: string): string {
	return name.includes(':') ? name.split(':').slice(1).join(':') : name;
}

// Prepared tag for fuzzysort searching
interface PreparedTag {
	tag: TagResponse;
	name: string;
	value: string;
	label: string;
}

export function TagEditor({
	open,
	onOpenChange,
	linkId,
	currentTags,
	onTagsChanged,
}: TagEditorProps) {
	// Use GraphQL query for tags - urql's cache handles deduplication across instances
	const { tags: allTags, loading: tagsLoading } = useTagsQuery({ includeNsfw: true });
	const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const selectorRef = useRef<{ focus: () => void } | null>(null);
	const addTagsMutation = useAddTagsToLinkMutation();
	const removeTagMutation = useRemoveTagFromLinkMutation();

	// Log tag loading for debugging
	useEffect(() => {
		if (open && !tagsLoading) {
			debug('loaded %d tags via GraphQL', allTags.length);
		}
	}, [open, tagsLoading, allTags.length]);

	// Sync selected options with current tags
	useEffect(() => {
		setSelectedOptions(currentTags.map(tagToOption));
	}, [currentTags]);

	// Focus selector when dialog opens
	useEffect(() => {
		if (open) {
			setTimeout(() => selectorRef.current?.focus(), 100);
		}
	}, [open]);

	// Helper to convert GraphQL tag edges to TagResponse format
	function edgesToTags(edges: Array<{ node: { id: string; name: string; color?: string | null; icon?: string | null; isNsfw: boolean; isCategory: boolean; isPersonal?: boolean | null } }>): TagResponse[] {
		return edges.map((edge) => ({
			id: decodeGlobalId(edge.node.id),
			name: edge.node.name,
			color: edge.node.color ?? null,
			icon: edge.node.icon ?? null,
			isNsfw: edge.node.isNsfw,
			isCategory: edge.node.isCategory,
			isPersonal: edge.node.isPersonal ?? false,
		}));
	}

	// Handle tag selection changes
	const handleChange = useCallback(
		async (newOptions: Option[]) => {
			const oldNames = new Set(selectedOptions.map((o) => o.value));
			const newNames = new Set(newOptions.map((o) => o.value));

			// Find added tags
			const added = newOptions.filter((o) => !oldNames.has(o.value));
			// Find removed tags
			const removed = selectedOptions.filter((o) => !newNames.has(o.value));

			setSelectedOptions(newOptions);

			// Process additions - always create as personal tags (only enricher/admin can create shared tags)
			if (added.length > 0) {
				debug(
					'adding tags: %o (personal)',
					added.map((o) => o.value)
				);
				setIsLoading(true);
				try {
					const result = await addTagsMutation.mutateAsync(
						linkId,
						added.map((o) => o.value),
						true // isPersonal
					);
					if (result?.link?.tags?.edges) {
						const updatedTags = edgesToTags(result.link.tags.edges);
						onTagsChanged(updatedTags);
					}
					// Invalidate cache since new tags may have been created
					invalidateTagCache();
				} catch (err) {
					debug('failed to add tags: %o', err);
					// Revert on error
					setSelectedOptions(currentTags.map(tagToOption));
				} finally {
					setIsLoading(false);
				}
			}

			// Process removals - pass isPersonal flag to remove correct tag type
			for (const removedOption of removed) {
				const tagToRemove = currentTags.find((t) => t.name === removedOption.value);
				if (tagToRemove) {
					debug(
						'removing tag: %s (id=%d, isPersonal=%s)',
						tagToRemove.name,
						tagToRemove.id,
						tagToRemove.isPersonal
					);
					setIsLoading(true);
					try {
						const result = await removeTagMutation.mutateAsync(
							linkId,
							tagToRemove.id,
							tagToRemove.isPersonal
						);
						if (result?.link?.tags?.edges) {
							const updatedTags = edgesToTags(result.link.tags.edges);
							onTagsChanged(updatedTags);
						}
					} catch (err) {
						debug('failed to remove tag: %o', err);
						// Revert on error
						setSelectedOptions(currentTags.map(tagToOption));
					} finally {
						setIsLoading(false);
					}
				}
			}
		},
		[linkId, selectedOptions, currentTags, onTagsChanged, addTagsMutation, removeTagMutation]
	);

	// Prepare tags for fuzzysort (memoized)
	const preparedTags = useMemo<PreparedTag[]>(
		() =>
			allTags.map((tag: TagResponse) => ({
				tag,
				name: tag.name,
				value: getTagValue(tag.name),
				label: formatTagLabel(tag.name),
			})),
		[allTags]
	);

	// Search for tags with fuzzysort
	const handleSearch = useCallback(
		async (searchValue: string): Promise<Option[]> => {
			if (!searchValue.trim()) {
				return allTags.map(tagToOption);
			}

			// Use fuzzysort with threshold to filter poor matches
			const results = fuzzysort.go(searchValue, preparedTags, {
				keys: ['name', 'value', 'label'],
				threshold: 0.4, // Cutoff: reject weak matches (e.g., "porn" won't match "platform:instagram")
				limit: 50,
			});

			return results.map((r) => tagToOption(r.obj.tag));
		},
		[allTags, preparedTags]
	);

	// Group tags by type for display
	const groupedOptions: Record<string, Option[]> = allTags.reduce(
		(acc: Record<string, Option[]>, tag: TagResponse) => {
			const group = getTagGroup(tag.name);
			if (!acc[group]) acc[group] = [];
			acc[group].push(tagToOption(tag));
			return acc;
		},
		{}
	);

	// Flatten grouped options for default display
	const defaultOptions: Option[] = Object.values(groupedOptions).flat();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Tags className="size-5" />
						Edit Tags
					</DialogTitle>
					<DialogDescription>
						Add or remove tags from this link. Type to search or create new tags.
					</DialogDescription>
				</DialogHeader>

				<div className="py-4">
					<MultipleSelector
						ref={
							selectorRef as React.RefObject<{
								selectedValue: Option[];
								input: HTMLInputElement;
								focus: () => void;
								reset: () => void;
							}>
						}
						value={selectedOptions}
						onChange={handleChange}
						defaultOptions={defaultOptions}
						onSearch={handleSearch}
						placeholder="Search or create tags..."
						creatable
						emptyIndicator={
							<p className="text-center text-sm text-muted-foreground py-4">
								No tags found. Type to create a new one.
							</p>
						}
						loadingIndicator={
							<p className="text-center text-sm text-muted-foreground py-4">Loading...</p>
						}
						badgeClassName="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20"
						commandProps={{
							className: 'max-h-[300px]',
						}}
						disabled={isLoading}
					/>
				</div>

				{/* Tag hints */}
				<div className="text-xs text-muted-foreground space-y-1 border-t pt-3">
					<p>
						<strong>Tips:</strong> Use prefixes for special tags:
					</p>
					<ul className="list-disc list-inside space-y-0.5 ml-2">
						<li>
							<code className="bg-muted px-1 rounded">category:videos</code> - Set category
						</li>
						<li>
							<code className="bg-muted px-1 rounded">domain:reddit.com</code> - Domain tag
						</li>
						<li>
							<code className="bg-muted px-1 rounded">reddit:u:username</code> - Reddit user
						</li>
					</ul>
				</div>
			</DialogContent>
		</Dialog>
	);
}
